import {
  DeviceFamily,
  MarketPlace,
  Placement,
  LocaleEnum,
  PresignedUrlAction,
  AssetType,
  TransformationVariant,
  TransformationStatus,
} from '@amzn/fire-tv-campaign-automation-service';
import { KatButton, KatFileUpload, KatInput, KatSpinner } from '@amzn/katal-react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { FC, useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { getUserAlias } from 'src/components/authentication/AuthenticationUtils';
import { SearchImageInput } from 'src/components/pages/campaignAutomation/input/assets/SearchImageInput';
import { LogoTextInput } from 'src/components/pages/campaignAutomation/input/assets/LogoTextInput';
import { StyledDropdown } from 'src/components/general/StyledDropdown';
import { getDeviceFamilyOptionsByStage } from 'src/config/deviceFamilyConfiguration';
import { getPlacementsForDeviceFamily } from 'src/config/deviceFamilyPlacementConfig';
import { ASSET_CONFIG } from 'src/configs/assetConfig';
import { CMSConfig } from 'src/configs/campaignMetadataConfig';
import { AssetConstants, ProvisionedConstants, STAGE } from 'src/constants';
import { useAuthentication } from 'src/contexts/AuthenticationContext';
import { getControlPlaneClient } from 'src/contexts/ClientContext';
import { useSelection } from 'src/contexts/SelectionContext';
import { useSmartUpload, WizardStep, ImageSource } from 'src/contexts/SmartUploadContext';
import { useToast } from 'src/contexts/ToastContext';
import { executeBatchTransform } from 'src/handlers/BatchTransformationHandler';
import { useAssetContext } from 'src/hooks/useAssetContext';
import { AssetStatus } from 'src/types';
import { PropUtils } from 'src/utils/PropUtils';
import { getVideoDuration } from 'src/utils/MediaUtils';
import {
  getPresignedUrls,
  transformAsset,
  getTransformedAsset,
} from 'src/utils/clients/controlPlane/apiMethods';
import { poll } from 'src/utils/Poller';

import styles from './SmartUploadWizard.module.css';

const bgConfig = ASSET_CONFIG[AssetType.BACKGROUND_IMAGE];
const MAX_VIDEO_DURATION = 21;

function generateS3Key(keyPrefix: string, file: File): string {
  const lastDot = file.name.lastIndexOf('.');
  const nameWithoutExt = lastDot > 0 ? file.name.substring(0, lastDot) : file.name;
  const extension = lastDot > 0 ? file.name.substring(lastDot + 1) : '';
  const sanitized =
    encodeURIComponent(nameWithoutExt)
      .replace(/%20/g, '_')
      .replace(/%[0-9A-Fa-f]{2}/g, '_')
      .replace(/[^a-zA-Z0-9_.-]/g, '_')
      .substring(0, AssetConstants.MAX_FILENAME_LENGTH) || 'unnamed';
  return `${keyPrefix}/${Date.now()}_${uuid()}_${sanitized}${extension ? '.' + extension : ''}`;
}

const STEP_ORDER = [
  WizardStep.DEVICE_FAMILY,
  WizardStep.PLACEMENT,
  WizardStep.MARKETPLACE,
  WizardStep.LOCALE,
  WizardStep.IMAGE_SOURCE,
  WizardStep.CATALOG,
  WizardStep.UPLOAD,
  WizardStep.ASSET_UPLOAD,
  WizardStep.RESULTS,
  WizardStep.LOGO,
  WizardStep.VIDEO,
];

const CompletedBadge: FC<{ label: string }> = ({ label }) => (
  <div className={styles.stepComplete}>
    <CheckCircleOutlineIcon className={styles.checkIcon} fontSize="small" />
    <span>{label}</span>
  </div>
);

const SmartUploadWizard: FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController>();
  const {
    wizardStep,
    setWizardStep,
    imageSource,
    setImageSource,
    batchResults,
    setBatchResults,
    updateBatchResult,
    logoTransformResult,
    setLogoTransformResult,
    selectedResultIndex,
    logoSource,
    setLogoSource,
    videoTransformResult,
    setVideoTransformResult,
    questionsCollapsed,
    setQuestionsCollapsed,
    wizardCollapsed,
    setWizardCollapsed,
  } = useSmartUpload();
  const {
    placement,
    deviceFamily,
    locale,
    marketplace,
    setPlacement,
    setDeviceFamily,
    setLocale,
    setMarketplace,
  } = useSelection();
  const { updateAssetState, assetStates } = useAssetContext();
  const logoState = assetStates[AssetType.LOGO];
  const controlPlaneClient = getControlPlaneClient();
  const toast = useToast();
  const { user } = useAuthentication();
  const actor = useMemo(() => getUserAlias(user), [user]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string>();
  const [catalogImageUrl, setCatalogImageUrl] = useState<string | null>(null);
  const [catalogImageTitle, setCatalogImageTitle] = useState<string | null>(null);
  const [videoStartTime, setVideoStartTime] = useState('00:00');
  const [videoEndTime, setVideoEndTime] = useState('00:21');
  const [videoClipping, setVideoClipping] = useState(false);
  const [videoClipResult, setVideoClipResult] = useState<{
    status: AssetStatus;
    error?: string;
  } | null>(null);
  const [videoDurationSec, setVideoDurationSec] = useState<number | null>(null);
  const [bgUploaded, setBgUploaded] = useState(false);
  const [bgS3Key, setBgS3Key] = useState<string | null>(null);

  const [wizardDeviceFamily, setWizardDeviceFamily] = useState<string | undefined>();
  const [wizardPlacement, setWizardPlacement] = useState<string | undefined>();
  const [wizardLocale, setWizardLocale] = useState<string | undefined>();
  const [wizardMarketplace, setWizardMarketplace] = useState<string | undefined>();

  const stage = ProvisionedConstants.STAGE as STAGE;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [wizardStep]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  // Auto-collapse questions when advancing past LOCALE
  useEffect(() => {
    if (STEP_ORDER.indexOf(wizardStep) >= STEP_ORDER.indexOf(WizardStep.IMAGE_SOURCE)) {
      setQuestionsCollapsed(true);
    }
  }, [wizardStep, setQuestionsCollapsed]);

  // Auto-collapse wizard when result is selected
  useEffect(() => {
    if (selectedResultIndex !== null) {
      setWizardCollapsed(true);
    }
  }, [selectedResultIndex, setWizardCollapsed]);

  const stepReached = (target: WizardStep): boolean =>
    STEP_ORDER.indexOf(wizardStep) >= STEP_ORDER.indexOf(target);

  const stepCompleted = (target: WizardStep): boolean =>
    STEP_ORDER.indexOf(wizardStep) > STEP_ORDER.indexOf(target);

  // --- Option builders ---
  const placementOptions = PropUtils.getTitleCasedOptions(
    PropUtils.getOptionsWithUnderscoresRemoved(
      PropUtils.createOptionsFromEnumValues(
        getPlacementsForDeviceFamily(deviceFamily, stage),
        Placement,
      ),
    ),
  );
  const deviceFamilyOptions = getDeviceFamilyOptionsByStage(stage);
  const localeOptions = PropUtils.createOptionsFromEnumValues(CMSConfig.LOCALE[marketplace] ?? []);
  const marketplaceOptions = (CMSConfig?.MARKET_PLACE ?? []).map((mp: string) => ({
    name: mp,
    value: mp,
  }));

  // --- Question step handlers ---
  const handleDeviceFamilyChange = useCallback(
    (e: { detail: { value: string } }) => {
      const value = e.detail.value as DeviceFamily;
      setWizardDeviceFamily(value);
      setDeviceFamily(value);
      setWizardStep(WizardStep.PLACEMENT);
    },
    [setDeviceFamily, setWizardStep],
  );

  const handlePlacementChange = useCallback(
    (e: { detail: { value: string } }) => {
      const value = e.detail.value as Placement;
      setWizardPlacement(value);
      setPlacement(value);
      setWizardStep(WizardStep.MARKETPLACE);
    },
    [setPlacement, setWizardStep],
  );

  const handleMarketplaceChange = useCallback(
    (e: { detail: { value: string } }) => {
      const value = e.detail.value;
      setWizardMarketplace(value);
      setMarketplace(value as MarketPlace);
      setWizardStep(WizardStep.LOCALE);
    },
    [setMarketplace, setWizardStep],
  );

  const handleLocaleChange = useCallback(
    (e: { detail: { value: string } }) => {
      const value = e.detail.value as LocaleEnum;
      setWizardLocale(value);
      setLocale(value);
      setWizardStep(WizardStep.IMAGE_SOURCE);
    },
    [setLocale, setWizardStep],
  );

  const handleImageSourceChoice = useCallback(
    (source: ImageSource) => {
      setImageSource(source);
      setWizardStep(source === 'catalog' ? WizardStep.CATALOG : WizardStep.UPLOAD);
    },
    [setImageSource, setWizardStep],
  );

  // --- Background image upload handler ---
  const handleFileAttached = useCallback(
    async (event: KatFileUpload.FilesAttachedEvent) => {
      const file = event.detail.files[0];
      if (!file) return;
      setUploading(true);
      setUploadError(undefined);
      try {
        const s3Key = generateS3Key(bgConfig.assetKeyPrefix, file);
        const [{ presignedUrl }] = await getPresignedUrls(
          controlPlaneClient,
          PresignedUrlAction.PUT,
          [s3Key],
        );
        const uploadResponse = await fetch(presignedUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        if (!uploadResponse.ok) throw new Error(`Upload failed: ${uploadResponse.status}`);
        updateAssetState(AssetType.BACKGROUND_IMAGE, {
          status: AssetStatus.PENDING_TRANSFORMATION,
          key: s3Key,
        });
        setBgUploaded(true);
        setBgS3Key(s3Key);
        setWizardStep(WizardStep.ASSET_UPLOAD);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        setUploadError(message);
        toast.addToast({ variant: 'danger', description: message });
      } finally {
        setUploading(false);
      }
    },
    [controlPlaneClient, toast, updateAssetState, setWizardStep],
  );

  // --- Catalog image handler ---
  const handleCatalogImageSelect = useCallback(
    async (imageUrl: string, imageTitle?: string) => {
      setCatalogImageUrl(imageUrl);
      setCatalogImageTitle(imageTitle ?? null);
      setUploading(true);
      setUploadError(undefined);
      try {
        const response = await fetch(imageUrl);
        if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
        const blob = await response.blob();
        const contentType = response.headers.get('content-type') || 'image/jpeg';
        const fileName = imageUrl.split('/').pop() || 'catalog_image';
        const file = new File([blob], fileName, { type: contentType });
        const s3Key = generateS3Key(bgConfig.assetKeyPrefix, file);
        const [{ presignedUrl }] = await getPresignedUrls(
          controlPlaneClient,
          PresignedUrlAction.PUT,
          [s3Key],
        );
        const uploadResponse = await fetch(presignedUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        if (!uploadResponse.ok) throw new Error(`Upload failed: ${uploadResponse.status}`);
        updateAssetState(AssetType.BACKGROUND_IMAGE, {
          status: AssetStatus.PENDING_TRANSFORMATION,
          key: s3Key,
        });
        setBgUploaded(true);
        setBgS3Key(s3Key);
        setWizardStep(WizardStep.ASSET_UPLOAD);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to process catalog image';
        setUploadError(message);
        toast.addToast({ variant: 'danger', description: message });
      } finally {
        setUploading(false);
      }
    },
    [controlPlaneClient, toast, updateAssetState, setWizardStep],
  );

  // --- Logo file upload handler ---
  const handleLogoFileAttached = useCallback(
    async (event: KatFileUpload.FilesAttachedEvent) => {
      const file = event.detail.files[0];
      if (!file) return;
      setUploading(true);
      setUploadError(undefined);
      try {
        const logoConfig = ASSET_CONFIG[AssetType.LOGO];
        const s3Key = generateS3Key(logoConfig.assetKeyPrefix, file);
        const [{ presignedUrl }] = await getPresignedUrls(
          controlPlaneClient,
          PresignedUrlAction.PUT,
          [s3Key],
        );
        const uploadResponse = await fetch(presignedUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        if (!uploadResponse.ok) throw new Error(`Upload failed: ${uploadResponse.status}`);
        updateAssetState(AssetType.LOGO, {
          status: AssetStatus.COMPLETED,
          key: s3Key,
          transformationVariant: TransformationVariant.NO_TRANSFORMATION,
        });
        setLogoTransformResult({
          variant: TransformationVariant.NO_TRANSFORMATION,
          status: AssetStatus.COMPLETED,
          transformedAssetKey: s3Key,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Logo upload failed';
        setUploadError(message);
        toast.addToast({ variant: 'danger', description: message });
      } finally {
        setUploading(false);
      }
    },
    [controlPlaneClient, toast, updateAssetState, setLogoTransformResult],
  );

  // --- Logo construct/save handler ---
  const handleLogoSave = useCallback(
    async (logoUpdate: Partial<typeof logoState>) => {
      updateAssetState(AssetType.LOGO, logoUpdate);
      const { logoTitle, cta, providerLogo, logoDescription } = { ...logoState, ...logoUpdate };
      if (!logoTitle || !cta || !providerLogo) return;
      setLogoTransformResult({
        variant: TransformationVariant.TEXT_BASED_TITLE_DESCRIPTION,
        status: AssetStatus.TRANSFORMATION_IN_PROGRESS,
      });
      try {
        const response = await transformAsset(controlPlaneClient, {
          assetType: AssetType.LOGO,
          transformationVariant: TransformationVariant.TEXT_BASED_TITLE_DESCRIPTION,
          placement,
          actor,
          cta,
          logoTitle,
          logoDescription,
          providerLogo,
        });
        const result = await poll(
          () => getTransformedAsset(controlPlaneClient, response.transformationId),
          {
            intervalSeconds: 10,
            maxRetries: 18,
            timeoutSeconds: 180,
            abortSignal: abortRef.current?.signal,
            successCondition: (r) =>
              r.status === TransformationStatus.TRANSFORMATION_COMPLETE ||
              r.status === TransformationStatus.TRANSFORMATION_FAILED,
          },
        );
        if (result.status === TransformationStatus.TRANSFORMATION_COMPLETE) {
          setLogoTransformResult({
            variant: TransformationVariant.TEXT_BASED_TITLE_DESCRIPTION,
            status: AssetStatus.COMPLETED,
            transformedAssetKey: result.transformedAssetKey ?? undefined,
          });
          updateAssetState(AssetType.LOGO, {
            status: AssetStatus.COMPLETED,
            key: result.transformedAssetKey ?? undefined,
          });
        } else {
          setLogoTransformResult({
            variant: TransformationVariant.TEXT_BASED_TITLE_DESCRIPTION,
            status: AssetStatus.FAILED,
            error: 'Logo transformation failed',
          });
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Logo transformation failed';
        setLogoTransformResult({
          variant: TransformationVariant.TEXT_BASED_TITLE_DESCRIPTION,
          status: AssetStatus.FAILED,
          error: message,
        });
        toast.addToast({ variant: 'danger', description: message });
      }
    },
    [
      controlPlaneClient,
      placement,
      actor,
      logoState,
      toast,
      updateAssetState,
      setLogoTransformResult,
    ],
  );

  // --- Video upload handler with auto-trim detection ---
  const handleVideoFileAttached = useCallback(
    async (event: KatFileUpload.FilesAttachedEvent) => {
      const file = event.detail.files[0];
      if (!file) return;
      setUploading(true);
      setUploadError(undefined);
      try {
        const duration = await getVideoDuration(file);
        setVideoDurationSec(duration);
        if (duration > MAX_VIDEO_DURATION) {
          setVideoStartTime('00:00');
          setVideoEndTime('00:21');
        }
        const videoConfig = ASSET_CONFIG[AssetType.VIDEO_TRAILER];
        const s3Key = generateS3Key(videoConfig.assetKeyPrefix, file);
        const [{ presignedUrl }] = await getPresignedUrls(
          controlPlaneClient,
          PresignedUrlAction.PUT,
          [s3Key],
        );
        const uploadResponse = await fetch(presignedUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        if (!uploadResponse.ok) throw new Error(`Upload failed: ${uploadResponse.status}`);
        updateAssetState(AssetType.VIDEO_TRAILER, {
          status: AssetStatus.COMPLETED,
          key: s3Key,
          transformationVariant: TransformationVariant.NO_TRANSFORMATION,
        });
        setVideoTransformResult({
          variant: TransformationVariant.NO_TRANSFORMATION,
          status: AssetStatus.COMPLETED,
          transformedAssetKey: s3Key,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Video upload failed';
        setUploadError(message);
        toast.addToast({ variant: 'danger', description: message });
      } finally {
        setUploading(false);
      }
    },
    [controlPlaneClient, toast, updateAssetState, setVideoTransformResult],
  );

  // --- Video clip handler ---
  const handleVideoClip = useCallback(async () => {
    if (!videoTransformResult?.transformedAssetKey) return;
    setVideoClipping(true);
    setVideoClipResult(null);
    try {
      const parseTime = (t: string) => {
        const [m, s] = t.split(':').map(Number);
        return (m || 0) * 60 + (s || 0);
      };
      const startSec = parseTime(videoStartTime);
      const endSec = parseTime(videoEndTime);
      const response = await transformAsset(controlPlaneClient, {
        assetType: AssetType.VIDEO_TRAILER,
        transformationVariant: TransformationVariant.CLIPPED_VIDEO,
        placement,
        actor,
        sourceAssetKey: videoTransformResult.transformedAssetKey,
        startTime: startSec,
        endTime: endSec,
      });
      const result = await poll(
        () => getTransformedAsset(controlPlaneClient, response.transformationId),
        {
          intervalSeconds: 10,
          maxRetries: 18,
          timeoutSeconds: 180,
          abortSignal: abortRef.current?.signal,
          successCondition: (r) =>
            r.status === TransformationStatus.TRANSFORMATION_COMPLETE ||
            r.status === TransformationStatus.TRANSFORMATION_FAILED,
        },
      );
      if (result.status === TransformationStatus.TRANSFORMATION_COMPLETE) {
        setVideoClipResult({ status: AssetStatus.COMPLETED });
        updateAssetState(AssetType.VIDEO_TRAILER, {
          status: AssetStatus.COMPLETED,
          key: result.transformedAssetKey ?? undefined,
          transformationVariant: TransformationVariant.CLIPPED_VIDEO,
        });
        setVideoTransformResult({
          variant: TransformationVariant.CLIPPED_VIDEO,
          status: AssetStatus.COMPLETED,
          transformedAssetKey: result.transformedAssetKey ?? undefined,
        });
      } else {
        setVideoClipResult({ status: AssetStatus.FAILED, error: 'Video clipping failed' });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Video clipping failed';
      setVideoClipResult({ status: AssetStatus.FAILED, error: message });
    } finally {
      setVideoClipping(false);
    }
  }, [
    controlPlaneClient,
    placement,
    actor,
    videoTransformResult,
    videoStartTime,
    videoEndTime,
    updateAssetState,
    setVideoTransformResult,
  ]);

  // --- Trigger batch transform (called when user proceeds from asset upload) ---
  const handleProceedToResults = useCallback(async () => {
    if (!bgS3Key) return;
    setBatchResults([]);
    setWizardStep(WizardStep.RESULTS);
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    await executeBatchTransform({
      sourceAssetKey: bgS3Key,
      placement,
      actor,
      controlPlaneClient,
      toast,
      onResultUpdate: updateBatchResult,
      abortSignal: abortRef.current.signal,
    });
  }, [
    bgS3Key,
    placement,
    actor,
    controlPlaneClient,
    toast,
    setBatchResults,
    setWizardStep,
    updateBatchResult,
  ]);

  // Auto-advance from RESULTS to done when result selected
  useEffect(() => {
    if (selectedResultIndex !== null && wizardStep === WizardStep.RESULTS) {
      // Result selected — wizard collapses via the wizardCollapsed effect above
    }
  }, [selectedResultIndex, wizardStep]);

  const logoReady = logoTransformResult?.status === AssetStatus.COMPLETED;
  const videoReady = videoTransformResult?.status === AssetStatus.COMPLETED;
  const allAssetsUploaded = bgUploaded && logoReady;

  // --- Wizard collapsed after result selection (Change 4) ---
  if (wizardCollapsed) {
    return (
      <div className={styles.wizardSummary} data-testid="smart-upload-wizard-collapsed">
        <span className={styles.wizardSummaryText}>
          ✅ Template selected — {wizardDeviceFamily} · {wizardPlacement} · {wizardMarketplace} ·{' '}
          {wizardLocale}
        </span>
        <KatButton
          variant="secondary"
          onClick={() => setWizardCollapsed(false)}
          data-testid="wizard-edit-result-btn"
        >
          Edit
        </KatButton>
      </div>
    );
  }

  return (
    <div
      className={`${styles.wizard} ${wizardCollapsed ? styles.wizardCollapsed : ''}`}
      ref={scrollRef}
      data-testid="smart-upload-wizard"
    >
      {/* --- Collapsed questions summary (Change 2) --- */}
      {questionsCollapsed ? (
        <div className={styles.collapsedSummary} data-testid="wizard-questions-collapsed">
          <div className={styles.summaryChips}>
            {wizardDeviceFamily && <span className={styles.summaryChip}>{wizardDeviceFamily}</span>}
            {wizardPlacement && <span className={styles.summaryChip}>{wizardPlacement}</span>}
            {wizardMarketplace && <span className={styles.summaryChip}>{wizardMarketplace}</span>}
            {wizardLocale && <span className={styles.summaryChip}>{wizardLocale}</span>}
          </div>
          <KatButton
            variant="secondary"
            onClick={() => setQuestionsCollapsed(false)}
            data-testid="wizard-edit-questions-btn"
          >
            Edit
          </KatButton>
        </div>
      ) : (
        <>
          {/* Step 1: Device Family */}
          <div className={styles.step}>
            <div className={styles.message}>
              Let&apos;s get started! Which device family are you targeting?
            </div>
            <div className={styles.userResponse}>
              <StyledDropdown
                options={deviceFamilyOptions}
                value={wizardDeviceFamily}
                onChange={handleDeviceFamilyChange}
                label="Device Family"
                placeholder="Select a device family"
                data-testid="wizard-device-family-selector"
              />
            </div>
            {stepCompleted(WizardStep.DEVICE_FAMILY) && (
              <CompletedBadge label={wizardDeviceFamily!} />
            )}
          </div>

          {/* Step 2: Placement */}
          {stepReached(WizardStep.PLACEMENT) && (
            <div className={styles.step}>
              <div className={styles.message}>Great! Which placement?</div>
              <div className={styles.userResponse}>
                <StyledDropdown
                  options={placementOptions}
                  value={wizardPlacement}
                  onChange={handlePlacementChange}
                  label="Placement"
                  placeholder="Select a placement"
                  data-testid="wizard-placement-selector"
                />
              </div>
              {stepCompleted(WizardStep.PLACEMENT) && <CompletedBadge label={wizardPlacement!} />}
            </div>
          )}

          {/* Step 3: Marketplace */}
          {stepReached(WizardStep.MARKETPLACE) && (
            <div className={styles.step}>
              <div className={styles.message}>Which marketplace?</div>
              <div className={styles.userResponse}>
                <StyledDropdown
                  options={marketplaceOptions}
                  value={wizardMarketplace}
                  onChange={handleMarketplaceChange}
                  label="Marketplace"
                  placeholder="Select a marketplace"
                  data-testid="wizard-marketplace-selector"
                />
              </div>
              {stepCompleted(WizardStep.MARKETPLACE) && (
                <CompletedBadge label={wizardMarketplace!} />
              )}
            </div>
          )}

          {/* Step 4: Locale */}
          {stepReached(WizardStep.LOCALE) && (
            <div className={styles.step}>
              <div className={styles.message}>And the locale?</div>
              <div className={styles.userResponse}>
                <StyledDropdown
                  options={localeOptions}
                  value={wizardLocale}
                  onChange={handleLocaleChange}
                  label="Locale"
                  placeholder="Select a locale"
                  data-testid="wizard-locale-selector"
                />
              </div>
              {stepCompleted(WizardStep.LOCALE) && <CompletedBadge label={wizardLocale!} />}
            </div>
          )}

          {STEP_ORDER.indexOf(wizardStep) >= STEP_ORDER.indexOf(WizardStep.IMAGE_SOURCE) && (
            <KatButton
              variant="primary"
              onClick={() => setQuestionsCollapsed(true)}
              data-testid="wizard-save-questions-btn"
            >
              Save
            </KatButton>
          )}
        </>
      )}

      {/* Step 5: Image Source */}
      {stepReached(WizardStep.IMAGE_SOURCE) && (
        <div className={styles.step}>
          <div className={styles.message}>
            Do you have an image to upload, or would you like to search our catalog?
          </div>
          <div className={styles.sourceButtons}>
            <KatButton
              variant={imageSource === 'upload' ? 'primary' : 'secondary'}
              onClick={() => handleImageSourceChoice('upload')}
              data-testid="wizard-source-upload"
            >
              Upload my image
            </KatButton>
            <KatButton
              variant={imageSource === 'catalog' ? 'primary' : 'secondary'}
              onClick={() => handleImageSourceChoice('catalog')}
              data-testid="wizard-source-catalog"
            >
              Search catalog
            </KatButton>
          </div>
          {stepCompleted(WizardStep.IMAGE_SOURCE) && (
            <CompletedBadge
              label={imageSource === 'upload' ? 'Upload my image' : 'Search catalog'}
            />
          )}
        </div>
      )}

      {/* Step 6: Catalog Search */}
      {stepReached(WizardStep.CATALOG) && imageSource === 'catalog' && (
        <div className={styles.step}>
          <div className={styles.message}>Search for a background image:</div>
          <SearchImageInput
            onImageSelect={handleCatalogImageSelect}
            selectedImageUrl={catalogImageUrl}
            selectedImageTitle={catalogImageTitle}
            onClearSelection={() => {
              setCatalogImageUrl(null);
              setCatalogImageTitle(null);
            }}
            disabled={uploading}
          />
        </div>
      )}

      {/* Step 7: Upload background */}
      {stepReached(WizardStep.UPLOAD) && imageSource === 'upload' && !bgUploaded && (
        <div className={styles.step}>
          <div className={styles.message}>Upload your background image.</div>
          <div className={styles.uploadArea}>
            <KatFileUpload
              accept={bgConfig.acceptedFileTypes}
              maxSize={bgConfig.globalMaxFileSizeInBytes}
              onFilesAttached={handleFileAttached}
              disabled={uploading}
              data-testid="wizard-file-upload"
            >
              {uploadError && <div slot="error">{uploadError}</div>}
            </KatFileUpload>
          </div>
        </div>
      )}

      {/* Change 3: Upload ALL assets before showing results */}
      {stepReached(WizardStep.ASSET_UPLOAD) && wizardStep === WizardStep.ASSET_UPLOAD && (
        <div className={styles.step}>
          <div className={styles.message}>
            Background uploaded! Now add your logo and video before we transform.
          </div>

          {/* Logo upload/construct */}
          <div className={styles.uploadSection}>
            <div className={styles.uploadSectionLabel}>Logo</div>
            <div className={styles.sourceButtons}>
              <KatButton
                variant={logoSource === 'upload' ? 'primary' : 'secondary'}
                onClick={() => setLogoSource('upload')}
                data-testid="wizard-logo-upload"
              >
                Upload a logo
              </KatButton>
              <KatButton
                variant={logoSource === 'construct' ? 'primary' : 'secondary'}
                onClick={() => setLogoSource('construct')}
                data-testid="wizard-logo-construct"
              >
                Construct it here
              </KatButton>
            </div>
            {logoSource === 'upload' && (
              <div className={styles.uploadArea}>
                <KatFileUpload
                  accept={ASSET_CONFIG[AssetType.LOGO].acceptedFileTypes}
                  maxSize={ASSET_CONFIG[AssetType.LOGO].globalMaxFileSizeInBytes}
                  onFilesAttached={handleLogoFileAttached}
                  disabled={uploading}
                  data-testid="wizard-logo-file-upload"
                >
                  {uploadError && <div slot="error">{uploadError}</div>}
                </KatFileUpload>
              </div>
            )}
            {logoSource === 'construct' && (
              <LogoTextInput
                logoTextState={logoState}
                updateAssetState={handleLogoSave}
                disabled={logoTransformResult?.status === AssetStatus.TRANSFORMATION_IN_PROGRESS}
              />
            )}
            {logoTransformResult?.status === AssetStatus.TRANSFORMATION_IN_PROGRESS && (
              <div className={styles.loading}>
                <KatSpinner size="small" />
                <span>Generating logo…</span>
              </div>
            )}
            {logoReady && <CompletedBadge label="Logo ready" />}
            {logoTransformResult?.status === AssetStatus.FAILED && (
              <div className={styles.message}>
                Logo failed: {logoTransformResult.error}. Please try again.
              </div>
            )}
          </div>

          {/* Video upload (optional) */}
          <div className={styles.uploadSection}>
            <div className={styles.uploadSectionLabel}>Video (optional)</div>
            {!videoTransformResult && (
              <div className={styles.uploadArea}>
                <KatFileUpload
                  accept={ASSET_CONFIG[AssetType.VIDEO_TRAILER].acceptedFileTypes}
                  maxSize={ASSET_CONFIG[AssetType.VIDEO_TRAILER].globalMaxFileSizeInBytes}
                  onFilesAttached={handleVideoFileAttached}
                  disabled={uploading}
                  data-testid="wizard-video-file-upload"
                >
                  {uploadError && <div slot="error">{uploadError}</div>}
                </KatFileUpload>
              </div>
            )}
            {/* Change 5: Video auto-trim handling */}
            {videoReady && videoDurationSec !== null && videoDurationSec <= MAX_VIDEO_DURATION && (
              <CompletedBadge label={`Video ready (${Math.round(videoDurationSec)}s)`} />
            )}
            {videoReady && videoDurationSec !== null && videoDurationSec > MAX_VIDEO_DURATION && (
              <>
                <div className={styles.autoTrimInfo}>
                  Video trimmed to first 21 seconds (max duration). Adjust if needed.
                </div>
                <div className={styles.userResponse}>
                  <KatInput
                    type="text"
                    label="Start Time (MM:SS)"
                    placeholder="00:00"
                    value={videoStartTime}
                    onInput={(e: KatInput.ChangeEvent) => setVideoStartTime(e.currentTarget.value)}
                    data-testid="wizard-video-start-time"
                  />
                  <KatInput
                    type="text"
                    label="End Time (MM:SS)"
                    placeholder="00:21"
                    value={videoEndTime}
                    onInput={(e: KatInput.ChangeEvent) => setVideoEndTime(e.currentTarget.value)}
                    data-testid="wizard-video-end-time"
                  />
                </div>
                <KatButton
                  onClick={handleVideoClip}
                  disabled={videoClipping}
                  data-testid="wizard-video-clip-btn"
                >
                  {videoClipping ? 'Clipping…' : 'Clip & Transform'}
                </KatButton>
                {videoClipping && <KatSpinner size="small" />}
                {videoClipResult?.status === AssetStatus.COMPLETED && (
                  <CompletedBadge label="Video clipped and ready" />
                )}
                {videoClipResult?.status === AssetStatus.FAILED && (
                  <div className={styles.message}>
                    Clipping failed: {videoClipResult.error}. Try again.
                  </div>
                )}
              </>
            )}
          </div>

          {/* Proceed to transform */}
          <KatButton
            variant="primary"
            onClick={handleProceedToResults}
            disabled={!allAssetsUploaded}
            data-testid="wizard-proceed-transform-btn"
          >
            {allAssetsUploaded ? 'Transform & Show Results' : 'Upload logo to continue'}
          </KatButton>
        </div>
      )}

      {/* Results step */}
      {stepReached(WizardStep.RESULTS) && (
        <div className={styles.step}>
          {batchResults.length > 0 &&
          batchResults.every((r) => r.status !== AssetStatus.TRANSFORMATION_IN_PROGRESS) ? (
            <div className={styles.message}>
              Transformation complete! Review and select a result in the preview panel.
            </div>
          ) : (
            <>
              <div className={styles.message}>Transforming your image… This may take a moment.</div>
              <div className={styles.loading}>
                <KatSpinner size="large" />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export { SmartUploadWizard };
